import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, Outlet, useMatch, useNavigate } from "react-router";
import { ApiError, api } from "@/lib/api";
import { resolveWorkspaceAccount } from "@/lib/account-workspace-resolve";
import type { ModulesResponse, WorkspaceModule } from "@/lib/modules";

const CURRENT_ACCOUNT_KEY = "c456.current_account_id";

export type WorkspaceAccount = {
  id: number;
  name: string;
  icon_key?: string | null;
  color?: string | null;
  owner_user_id: number;
  enterprise_verified_at: string | null;
  /** membership = 本用户成员账户；authorization = 通过代管授权进入 */
  access_kind?: "membership" | "authorization";
  permission?: "read_only" | "maintain" | null;
};

export type WorkspaceBrand = {
  id: number;
  name: string;
  icon_key?: string | null;
  color?: string | null;
  discarded_at?: string | null;
  discard_expires_at?: string | null;
  tools?: { id: number; product: string; name: string }[];
};

type AccountWorkspaceValue = {
  accounts: WorkspaceAccount[];
  account: WorkspaceAccount | null;
  accountId: string;
  brands: WorkspaceBrand[];
  modules: WorkspaceModule[];
  /** membership = 本用户成员账户；authorization = 通过收到的授权进入 */
  accessKind: "membership" | "authorization" | null;
  isLoading: boolean;
  error: string | null;
  createAccountOpen: boolean;
  setCreateAccountOpen: (open: boolean) => void;
  openCreateAccount: () => void;
  createBrandOpen: boolean;
  setCreateBrandOpen: (open: boolean) => void;
  openCreateBrand: () => void;
  refreshAccounts: () => Promise<WorkspaceAccount[]>;
  refreshBrands: () => Promise<WorkspaceBrand[]>;
  refreshModules: () => Promise<WorkspaceModule[]>;
  switchAccount: (id: number) => void;
};

const AccountWorkspaceContext = createContext<AccountWorkspaceValue | null>(null);

export function useAccountWorkspace() {
  const context = useContext(AccountWorkspaceContext);
  if (!context) {
    throw new Error("useAccountWorkspace must be used within AccountWorkspaceProvider");
  }
  return context;
}

export function useOptionalAccountWorkspace() {
  return useContext(AccountWorkspaceContext);
}

export function rememberAccountId(id: number | string) {
  localStorage.setItem(CURRENT_ACCOUNT_KEY, String(id));
}

export function readRememberedAccountId(): string | null {
  return localStorage.getItem(CURRENT_ACCOUNT_KEY);
}

function useCurrentAccountId() {
  const match = useMatch({ path: "/accounts/:accountId/*", end: false });
  const exact = useMatch({ path: "/accounts/:accountId", end: true });
  const id = match?.params.accountId ?? exact?.params.accountId ?? "";
  return id && id !== "new" ? id : "";
}

export function AccountWorkspaceProvider({ children }: { children: ReactNode }) {
  const accountId = useCurrentAccountId();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<WorkspaceAccount[]>([]);
  const [brands, setBrands] = useState<WorkspaceBrand[]>([]);
  const [modules, setModules] = useState<WorkspaceModule[]>([]);
  const [authorizedAccount, setAuthorizedAccount] = useState<WorkspaceAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isResolvingAuthorized, setIsResolvingAuthorized] = useState(false);
  const [authorizedResolved, setAuthorizedResolved] = useState(false);
  /** 代管详情校验失败的 accountId；仅当与当前 URL 一致时才视为拒绝 */
  const [deniedAccountId, setDeniedAccountId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [createBrandOpen, setCreateBrandOpen] = useState(false);

  const openCreateAccount = useCallback(() => setCreateAccountOpen(true), []);
  const openCreateBrand = useCallback(() => setCreateBrandOpen(true), []);

  const refreshAccounts = useCallback(async () => {
    const { accounts: fetched } = await api.fetch<{ accounts: WorkspaceAccount[] }>("/accounts");
    setAccounts(fetched);
    return fetched;
  }, []);

  const refreshBrands = useCallback(async () => {
    if (!accountId) {
      setBrands([]);
      return [];
    }
    const { brands: fetched } = await api.fetchForAccount<{ brands: WorkspaceBrand[] }>(
      accountId,
      `/accounts/${accountId}/brands`,
    );
    setBrands(fetched);
    return fetched;
  }, [accountId]);

  const refreshModules = useCallback(async () => {
    if (!accountId) {
      setModules([]);
      return [];
    }
    const payload = await api.fetchForAccount<ModulesResponse>(accountId, "/api/modules");
    setModules(payload.modules);
    return payload.modules;
  }, [accountId]);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    refreshAccounts()
      .catch((caughtError) => {
        setError(caughtError instanceof ApiError ? caughtError.message : "无法加载账户列表");
      })
      .finally(() => setIsLoading(false));
  }, [refreshAccounts]);

  useEffect(() => {
    if (!accountId) {
      setBrands([]);
      setModules([]);
      return;
    }
    refreshBrands().catch(() => setBrands([]));
    refreshModules().catch(() => setModules([]));
  }, [accountId, refreshBrands, refreshModules]);

  useEffect(() => {
    if (accountId) rememberAccountId(accountId);
  }, [accountId]);

  const membershipAccount = useMemo(
    () => accounts.find((item) => String(item.id) === accountId && item.access_kind !== "authorization") ?? null,
    [accounts, accountId],
  );

  const listedAuthorizedAccount = useMemo(
    () => accounts.find((item) => String(item.id) === accountId && item.access_kind === "authorization") ?? null,
    [accounts, accountId],
  );

  // 首帧即可从列表解析代管账户；authorizedAccount 仅作 API 富化覆盖
  const { account, accessKind } = useMemo(
    () =>
      resolveWorkspaceAccount({
        accounts,
        accountId,
        authorizedAccount,
        authorizationDenied: deniedAccountId === accountId,
      }),
    [accounts, accountId, authorizedAccount, deniedAccountId],
  );

  useEffect(() => {
    if (!accountId || isLoading) return;
    if (membershipAccount) {
      setAuthorizedAccount(null);
      setAuthorizedResolved(true);
      setDeniedAccountId(null);
      setIsResolvingAuthorized(false);
      return;
    }

    let cancelled = false;
    setIsResolvingAuthorized(true);
    setAuthorizedResolved(false);
    setDeniedAccountId(null);
    // 列表乐观项已由 resolveWorkspaceAccount 同步使用；此处拉取详情覆盖
    if (listedAuthorizedAccount) {
      setAuthorizedAccount(listedAuthorizedAccount);
    }

    api
      .fetchForAccount<{ account: WorkspaceAccount }>(accountId, `/accounts/${accountId}`)
      .then(({ account: fetched }) => {
        if (cancelled) return;
        setAuthorizedAccount({
          ...fetched,
          access_kind: "authorization",
          permission: listedAuthorizedAccount?.permission ?? fetched.permission ?? null,
        });
        setDeniedAccountId(null);
        setAuthorizedResolved(true);
      })
      .catch((caughtError) => {
        if (cancelled) return;
        setAuthorizedAccount(null);
        setDeniedAccountId(accountId);
        setAuthorizedResolved(true);
        if (caughtError instanceof ApiError && caughtError.code === "membership_disabled") {
          setError("你在该账户的成员资格已禁用，请切换到其他账户");
        }
      })
      .finally(() => {
        if (!cancelled) setIsResolvingAuthorized(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, isLoading, membershipAccount, listedAuthorizedAccount]);

  const switchAccount = useCallback(
    (id: number) => {
      rememberAccountId(id);
      navigate(`/accounts/${id}`);
    },
    [navigate],
  );

  const workspaceLoading =
    isLoading ||
    Boolean(
      accountId &&
        !membershipAccount &&
        isResolvingAuthorized &&
        !listedAuthorizedAccount &&
        !authorizedResolved,
    );

  const value = useMemo(
    () => ({
      accounts,
      account,
      accountId,
      brands,
      modules,
      accessKind,
      isLoading: workspaceLoading,
      error,
      createAccountOpen,
      setCreateAccountOpen,
      openCreateAccount,
      createBrandOpen,
      setCreateBrandOpen,
      openCreateBrand,
      refreshAccounts,
      refreshBrands,
      refreshModules,
      switchAccount,
    }),
    [
      accounts,
      account,
      accountId,
      brands,
      modules,
      accessKind,
      workspaceLoading,
      error,
      createAccountOpen,
      openCreateAccount,
      createBrandOpen,
      openCreateBrand,
      refreshAccounts,
      refreshBrands,
      refreshModules,
      switchAccount,
    ],
  );

  return <AccountWorkspaceContext.Provider value={value}>{children}</AccountWorkspaceContext.Provider>;
}

export function AccountWorkspaceLayout() {
  return <AccountWorkspaceGate />;
}

function AccountWorkspaceGate() {
  const { accountId, accounts, account, isLoading, error } = useAccountWorkspace();

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">正在加载账户工作区…</p>;
  }

  if (error) {
    return (
      <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
        {error}
      </p>
    );
  }

  if (!accountId) {
    return <Navigate to="/" replace />;
  }

  if (!account) {
    if (accounts.length === 0) {
      return <Navigate to="/onboarding" replace />;
    }
    // 无权限进入当前 URL 账户时回退到自有账户
    const fallback =
      accounts.find((item) => item.access_kind !== "authorization") ?? accounts[0];
    return <Navigate to={`/accounts/${fallback.id}`} replace />;
  }

  return <Outlet />;
}
